import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { format, subMonths } from "date-fns";

export default function ComparisonChart({ bloodTests, timeRange }) {
  // Process data for comparison chart
  const getComparisonData = () => {
    if (!bloodTests || bloodTests.length === 0) return [];

    const now = new Date();
    let months;

    switch (timeRange) {
      case "3months":
        months = 3;
        break;
      case "6months":
        months = 6;
        break;
      case "1year":
        months = 12;
        break;
      default:
        months = 6;
    }

    // Group tests by month
    const monthlyData = {};

    for (let i = months - 1; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthKey = format(monthDate, "MMM yyyy");
      monthlyData[monthKey] = {
        month: monthKey,
        tests: 0,
        glucose: [],
        cholesterol_total: [],
        hemoglobin: [],
      };
    }

    bloodTests.forEach((test) => {
      const testMonth = format(new Date(test.test_date), "MMM yyyy");
      if (monthlyData[testMonth]) {
        monthlyData[testMonth].tests += 1;

        if (test.results) {
          if (test.results.glucose)
            monthlyData[testMonth].glucose.push(test.results.glucose);
          if (test.results.cholesterol_total)
            monthlyData[testMonth].cholesterol_total.push(
              test.results.cholesterol_total
            );
          if (test.results.hemoglobin)
            monthlyData[testMonth].hemoglobin.push(test.results.hemoglobin);
        }
      }
    });

    // Calculate averages
    return Object.values(monthlyData).map((data) => ({
      month: data.month,
      tests: data.tests,
      glucose:
        data.glucose.length > 0
          ? Math.round(
              data.glucose.reduce((a, b) => a + b, 0) / data.glucose.length
            )
          : 0,
      cholesterol:
        data.cholesterol_total.length > 0
          ? Math.round(
              data.cholesterol_total.reduce((a, b) => a + b, 0) /
                data.cholesterol_total.length
            )
          : 0,
      hemoglobin:
        data.hemoglobin.length > 0
          ? (
              data.hemoglobin.reduce((a, b) => a + b, 0) /
              data.hemoglobin.length
            ).toFixed(1)
          : 0,
    }));
  };

  const comparisonData = getComparisonData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-lg shadow-xl border border-slate-200">
          <p className="font-medium text-slate-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}{" "}
              {entry.name === "tests"
                ? "tests"
                : entry.dataKey === "hemoglobin"
                  ? "g/dL"
                  : "mg/dL"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Monthly Health Summary
        </CardTitle>
        <p className="text-sm text-slate-600">
          Average values and test frequency over {timeRange}
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={comparisonData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="month"
                stroke="#64748b"
                fontSize={12}
                tick={{ fill: "#64748b" }}
              />
              <YAxis
                stroke="#64748b"
                fontSize={12}
                tick={{ fill: "#64748b" }}
              />
              <Tooltip content={<CustomTooltip />} />

              <Bar
                dataKey="glucose"
                name="Glucose (mg/dL)"
                fill="#3b82f6"
                radius={[2, 2, 0, 0]}
                opacity={0.8}
              />
              <Bar
                dataKey="cholesterol"
                name="Cholesterol (mg/dL)"
                fill="#8b5cf6"
                radius={[2, 2, 0, 0]}
                opacity={0.8}
              />
              <Bar
                dataKey="hemoglobin"
                name="Hemoglobin (g/dL)"
                fill="#10b981"
                radius={[2, 2, 0, 0]}
                opacity={0.8}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-slate-600">Glucose</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-purple-500 rounded"></div>
            <span className="text-slate-600">Cholesterol</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span className="text-slate-600">Hemoglobin</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
